import { Alert } from "@/components/shared/alerts/Alert";
import { ActionButton } from "@/components/shared/buttons/ActionButton";
import { Close as CloseIcon } from "@mui/icons-material";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
    styled,
    useTheme,
} from "@mui/material";
import React, { useMemo } from "react";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiBackdrop-root": {
        backgroundColor: "rgba(15, 23, 42, 0.4)",
        backdropFilter: "blur(4px)",
    },
    "& .MuiDialog-paper": {
        borderRadius: "12px",
        minWidth: "400px",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow:
            "0 20px 60px -12px rgba(0, 0, 0, 0.15), 0 8px 20px -8px rgba(0, 0, 0, 0.08)",
        [theme.breakpoints.down("sm")]: {
            minWidth: "unset",
            margin: theme.spacing(2),
        },
    },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: theme.spacing(2.5, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: `${theme.spacing(3)} !important`,
    overflowY: "auto",
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
}));

export interface IModalAction {
    label: string;
    onClick: () => void;
    variant?: "contained" | "outlined" | "text";
    color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
    disabled?: boolean;
}

export interface IModalAlert {
    severity: "error" | "warning" | "info" | "success";
    message: string | React.ReactNode;
}

const SEVERITY_ORDER: Record<string, number> = {
    error: 0,
    warning: 1,
    info: 2,
    success: 3,
};

export interface ModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string | React.ReactNode;
    children: React.ReactNode;
    actions?: IModalAction[];
    maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
    showCancel?: boolean;
    cancelLabel?: string;
    onCancel?: () => void;
    footerText?: string | React.ReactNode;
    isLoading?: boolean;
    error?: string | null;
    alerts?: IModalAlert[];
}

export const Modal: React.FC<ModalProps> = ({
    open,
    onClose,
    title,
    subtitle,
    children,
    actions = [],
    maxWidth = "sm",
    showCancel = true,
    cancelLabel = "Cancel",
    isLoading = false,
    onCancel,
    footerText,
    error,
    alerts,
}) => {
    const theme = useTheme();
    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        } else {
            onClose();
        }
    };

    const resolvedAlerts = useMemo(() => {
        const result: IModalAlert[] = [];
        if (error) {
            result.push({ severity: "error", message: error });
        }
        if (alerts) {
            result.push(...alerts);
        }
        result.sort(
            (a, b) =>
                (SEVERITY_ORDER[a.severity] ?? 9) -
                (SEVERITY_ORDER[b.severity] ?? 9)
        );
        return result;
    }, [error, alerts]);

    return (
        <StyledDialog
            open={open}
            onClose={isLoading ? undefined : onClose}
            maxWidth={maxWidth}
            fullWidth={true}
            disableRestoreFocus
        >
            <StyledDialogTitle>
                <Stack>
                    <Typography
                        variant="h6"
                        component="p"
                        sx={{ fontWeight: 600, fontSize: "1.125rem" }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                wordBreak: "break-word",
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Stack>
                <IconButton
                    onClick={onClose}
                    disabled={isLoading}
                    size="small"
                    sx={{
                        color: theme.palette.text.secondary,
                        "&:hover": {
                            backgroundColor: theme.palette.grey[100],
                        },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </StyledDialogTitle>

            <LinearProgress sx={{ display: isLoading ? "block" : "none" }} />

            <StyledDialogContent>
                {resolvedAlerts.length > 0 && (
                    <Stack spacing={1} sx={{ mb: 2 }}>
                        {resolvedAlerts.map((alert, i) => (
                            <Alert key={i} severity={alert.severity} compact>
                                {alert.message}
                            </Alert>
                        ))}
                    </Stack>
                )}
                {children}
            </StyledDialogContent>

            {(showCancel || actions.length > 0 || footerText) && (
                <StyledDialogActions>
                    {footerText && (
                        <div
                            style={{
                                marginRight: "auto",
                                fontSize: "0.75rem",
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {typeof footerText === "string" ? (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: "0.75rem",
                                    }}
                                >
                                    {footerText}
                                </Typography>
                            ) : (
                                footerText
                            )}
                        </div>
                    )}
                    {showCancel && (
                        <ActionButton
                            onClick={handleCancel}
                            variant="outlined"
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                color: theme.palette.text.secondary,
                                border: `1px solid ${theme.palette.divider}`,
                                "&:hover": {
                                    backgroundColor: theme.palette.grey[50],
                                    borderColor: theme.palette.grey[400],
                                },
                            }}
                        >
                            {cancelLabel}
                        </ActionButton>
                    )}
                    {actions.map((action, index) => (
                        <ActionButton
                            key={index}
                            onClick={action.onClick}
                            disabled={action.disabled}
                            variant={action.variant || "contained"}
                            color={action.color || "primary"}
                            sx={{
                                ...(action.color === "primary" && {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    "&:hover": {
                                        backgroundColor:
                                            theme.palette.primary.dark,
                                    },
                                }),
                            }}
                        >
                            {action.label}
                        </ActionButton>
                    ))}
                </StyledDialogActions>
            )}
        </StyledDialog>
    );
};
